import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDate, formatWibLong } from "@po/shared";
import type { KursUpdate } from "../../../api/src/app";
import { DataTable } from "../components/DataTable";
import { api } from "../lib/api";
import { useSyncNow } from "../lib/sync-now";
import { useDashboard } from "../pages/Dashboard";

/** Sertica's Company (SPEC §8.9): every company, not only the user's units'. */
export function CompaniesTab() {
  return (
    <div className="space-y-4">
      <p className="text-[11.5px] text-muted">
        The companies POs are issued under, with their approval rules. Only the search applies here:
        it matches the number, name, city and country. POs counts your own, in your units.
      </p>
      <DataTable table="companies" title="Companies" lens="company" />
    </div>
  );
}

type KursStatus = {
  last: (KursUpdate & { at: string; trigger: string | null }) | null;
  error: { at: string; message: string | null } | null;
};

/** Sertica's Currency (SPEC §8.9): each currency's current rate to USD, set weekly from KMK (XE for the rest). */
export function CurrenciesTab() {
  return (
    <div className="space-y-4">
      <p className="text-[11.5px] text-muted">
        Each currency&apos;s current rate as Sertica keeps it: what 100 units are worth in USD.
        Sertica keeps no rate history, so the change is against the last rate the dashboard
        recorded.
      </p>
      <KursPanel />
      <DataTable
        table="currencies"
        title="Currencies"
        lens="plain"
        highlight={(r) =>
          String(r.kmk ?? "").startsWith("Not") ? ["rate", "perUsd", "rateDate", "kmk"] : []
        }
      />
    </div>
  );
}

/** The weekly KMK rate update: which KMK Sertica's rates come from, and for admins "Update rates from KMK". */
function KursPanel() {
  const { me } = useDashboard();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["kurs"],
    queryFn: () => api<KursStatus>("/currencies/kurs"),
    refetchInterval: 5 * 60_000,
  });
  const { state, start, busy } = useSyncNow(() => void qc.invalidateQueries(), "/admin/kurs");
  const last = q.data?.last;
  return (
    <div className="border border-line bg-card px-4 py-3 text-[12.5px] space-y-1.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="font-semibold">Rates from the KMK tax rates (Kurs Pajak)</span>
        <span className="text-muted">Set in Sertica TEST every Wednesday at 09:00 WIB</span>
        {me.isAdmin && (
          <button
            className="ml-auto rounded-input bg-brand-2 text-white font-semibold px-3 py-1 inline-flex items-center gap-2"
            disabled={busy}
            onClick={start}
          >
            {busy && (
              <span
                aria-hidden
                className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"
              />
            )}
            {busy ? "Updating…" : "Update rates from KMK"}
          </button>
        )}
      </div>
      {last ? (
        <p>
          KMK {last.kmkNo}, in force {formatDate(last.from)} – {formatDate(last.to)} (USD 1 = Rp
          {last.usd?.toLocaleString("id-ID")}): checked {formatWibLong(new Date(last.at))}
          {last.trigger === "button" ? " on request" : ""},{" "}
          {last.changed.length
            ? `${last.changed.length} rate${last.changed.length > 1 ? "s" : ""} changed`
            : "every rate already matched"}
          .
          {last.xe && Object.keys(last.xe.rates).length > 0 && (
            <span className="text-muted">
              {" "}
              Not in KMK, from XE&apos;s mid-market rate ({formatWibLong(
                new Date(last.xe.at),
              )}): {Object.keys(last.xe.rates).join(", ")}.
            </span>
          )}
          {last.skipped.length > 0 && (
            <span className="text-muted">
              {" "}
              Not in KMK or XE, left as entered by hand (highlighted below):{" "}
              {last.skipped.join(", ")}.
            </span>
          )}
        </p>
      ) : (
        q.isSuccess && <p className="text-muted">No update has run yet.</p>
      )}
      {q.data?.error && (
        <p className="text-[var(--danger)]">
          The last update failed ({formatWibLong(new Date(q.data.error.at))}):{" "}
          {q.data.error.message}
        </p>
      )}
      <span role="status">
        {state.kind === "done" && (
          <span className="text-[var(--stage-received)]">Rates updated.</span>
        )}
        {state.kind === "failed" && <span className="text-[var(--danger)]">{state.message}</span>}
      </span>
    </div>
  );
}

/** Sertica's Department (SPEC §8.10): a row opens the user's requisitions in that department. */
export function DepartmentsTab() {
  const { setSearch } = useDashboard();
  return (
    <div className="space-y-4">
      <p className="text-[11.5px] text-muted">
        Every department in Sertica, on board and in the office, with your own requisitions and POs
        in it. Click one to see its requisitions. Only the search applies here: it matches the
        number, name and remark.
      </p>
      <DataTable
        table="departments"
        title="Departments"
        lens="plain"
        onRow={(r) =>
          r.name && setSearch({ tab: "requisitions", dept: [String(r.name)], q: undefined })
        }
      />
    </div>
  );
}
